import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import '@/assets/styles/views/coach/student-details.scss';

import StudentBg from '@/assets/images/student-detail-bg.png';

import { type Student, Tab } from './student-details.types';
import { StudentPerformance, StudentMeetings, StudentExams } from '@/components';
import { useLocation, useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import StudentMissions from '@/components/student-missions/student-missions.tsx';
import StudentSupport from './_components/StudentSupport.tsx';
import { useFeatureFlag } from 'configcat-react';
import StudentPackageInfoModal from '@/components/student-package-info-modal';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useApi } from '@/api/useApi.tsx';
import { formatStudentDates } from '@/utility/formatters/studentInfoFormatter.ts';
import { sendMessageToWebView } from '@/utility/webview.ts';
import { PostMessage } from '@/constants/post-message.ts';
import { CONFIGCAT_KEYS } from '@/constants/configcat-keys.ts';
import { jsonTryParse } from '@/utility';

const StudentDashboardScreen: React.FC = () => {
	const { value: showMission } = useFeatureFlag('showMission', false);
	const { value: enterTestResultStr } = useFeatureFlag(CONFIGCAT_KEYS.ENTER_TEST_RESULT, '{}');
	const { value: isActiveStudentDetailSupportTab } = useFeatureFlag(CONFIGCAT_KEYS.IS_ACTIVE_STUDENT_DETAIL_SUPPORT_TAB, false);
	const { StudentStatisticControllerApi } = useApi();

	const [activeTab, setActiveTab] = useState<string>('performance');
	const [tabs, setTab] = useState<Tab[]>([
		{ id: 'meetings', name: 'Görüşmeler', enabled: true },
        { id: 'performance', name: 'Performans Raporu', enabled: true },
        { id: 'exams', name: 'Deneme Sınavları', enabled: true }
	]);

	const [isOpenPackageInfoModal, setIsOpenPackageInfoModal] = useState(false);

	const { state } = useLocation();
	const navigate = useNavigate();

	const { data: studentResponse } = useQueryProvider(
		[QUERY_KEYS.findStudentDetailedStatisticsOfLoggedInExpert, state?.studentId],
		() => {
			return StudentStatisticControllerApi.findStudentDetailedStatisticsOfLoggedInExpert(
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				undefined,
				state?.studentId,
				undefined,
				undefined,
				undefined,
				undefined,
				['lastInteractionDate,desc']
			);
		},
		{
			enabled: !!state?.studentId
		}
	);

	const student: Student = useMemo(() => {
		if (!studentResponse?.data?.data?.content) {
			return {
				name: '',
				grade: '',
				avatar: undefined
			};
		}

		const stat = studentResponse.data.data.content[0];
		const { formattedDate, isMoreThan21Days } = formatStudentDates(stat?.lastInteractionDate);
		const { formattedDate: formattedMatchingDate } = formatStudentDates(stat?.matchingDate);

		return {
			id: stat?.studentId?.toString() as string,
			shortName: `${stat?.studentName?.charAt(0)?.toUpperCase() || ''} ${stat?.studentSurname?.charAt(0)?.toUpperCase() || ''}`,
			name: `${stat?.studentName || ''} ${stat?.studentSurname || ''}`,
			grade: stat?.preferredGradeName || '-',
			gradeId: stat?.preferredGradeId || undefined,
			avatar: stat?.avatarUrl || undefined,
			meetingCount: stat?.totalInteractionCount || 0,
			lastSolved: stat?.cancelledAppointmentCount || '-',
			lastMeeting: formattedDate,
			isHighlighted: isMoreThan21Days,
			matchingDate: formattedMatchingDate,
			isTopRanked: stat?.isTopRanked
		};
	}, [studentResponse]);

	const handleTabClick = (tabId: string) => {
		setActiveTab(tabId);
	};

	const handleMessageClick = () => {
		navigate(NavigationPath.messages, {
			state: { studentId: student?.id }
		});
	};

	const handleOpenExamForm = () => {
		const url = enterTestResultConfig?.formLink;
		if (!url) return;
		const sent = sendMessageToWebView({
			type: PostMessage.openExternalLink,
			data: { link: url }
		});
		if (!sent) {
			window.open(url, '_blank');
		}
	};

	const enterTestResultConfig = useMemo(() => {
		return jsonTryParse(enterTestResultStr) as { isActive: boolean; formLink: string } | null;
	}, [enterTestResultStr]);

	useEffect(() => {
		if (state && state?.openMeetingsTab) {
			setActiveTab('meetings');
		}
	}, [state]);

	useEffect(() => {
		setTab(parent => {
			const hasSupport = parent.some(t => t.id === 'support');
			if (isActiveStudentDetailSupportTab && !hasSupport) {
				return [...parent, { id: 'support', name: 'Destek', enabled: true }];
			}
			if (!isActiveStudentDetailSupportTab && hasSupport) {
				return parent.filter(t => t.id !== 'support');
			}
			return parent;
		});
	}, [isActiveStudentDetailSupportTab]);

	useEffect(() => {
		if (showMission) {
			setTab(parent => {
				const hasMissions = parent.some(t => t.id === 'missions');
				if (hasMissions) return parent;
				const supportIndex = parent.findIndex(t => t.id === 'support');
				const insertAt = supportIndex >= 0 ? supportIndex : parent.length;
				const next = [...parent];
				next.splice(insertAt, 0, { id: 'missions', name: 'Görevler', enabled: true });
				return next;
			});
		}
	}, [showMission]);

	useEffect(() => {
		setTimeout(() => {
			const container = document.querySelector('.main-content-container');
			if (container) {
				container.scrollTop = 0;
			}
		}, 100);
	}, []);

	return (
		<div className="student-dashboard">
			{/* Top Profile Section */}
			<div className="student-dashboard__profile">
				<img
					src={StudentBg}
					className="student-dashboard__profile-bg"
				/>
				<div className="student-dashboard__profile-top">
					<button
						className="back-button"
						onClick={() => {
							window.history.back();
						}}>
						<i className="icons-arrow-left" />
					</button>
				</div>
				<div className="student-dashboard__profile-bottom">
					{/* Profile Image and Info Section */}
					<div className="student-dashboard__profile-top-section">
						{student?.avatar ? (
							<div className="student-dashboard__profile-image">
								<img
									src={student?.avatar}
									alt={student?.name}
								/>
							</div>
						) : (
							<div className="student-dashboard__profile-image student-dashboard__profile-image--placeholder">{student?.shortName}</div>
						)}

						<div className="student-dashboard__profile-info">
							<div className="student-dashboard__profile-name-wrapper">
								<div className="student-dashboard__profile-name">{student?.name}</div>
								<If condition={!!student?.isTopRanked}>
									<div className="student-dashboard__profile-top-ranked">
										<i className="icons-medal" />
										Derece Öğrencisi
									</div>
								</If>
							</div>

							<div className="student-dashboard__profile-grade">{student?.grade}</div>
							<div className="student-dashboard__profile-package-action">
								<button
									className="btn-open-package-info"
									onClick={() => setIsOpenPackageInfoModal(true)}>
									<i className="icons-cap-full" />
									Paket Bilgilerini Gör
								</button>
							</div>
						</div>
					</div>

					{/* Stats and Actions Section */}
					<div className="student-dashboard__profile-bottom-section">
						<div className="student-dashboard__profile-stats">
							<div className="student-dashboard__profile-stats-item">
								<div className="student-dashboard__profile-stats-item-label">Görüşme Sayısı</div>
								<div className="student-dashboard__profile-stats-item-value">{student?.meetingCount || ''}</div>
							</div>
							<div className="student-dashboard__profile-stats-divider" />
							<div className="student-dashboard__profile-stats-item">
								<div className="student-dashboard__profile-stats-item-label">Son Görüşme</div>
								<div className="student-dashboard__profile-stats-item-value">{student?.lastMeeting || ''}</div>
							</div>
						</div>

						<div className="student-dashboard__profile-actions">
							<button
								className="btn btn-primary"
								onClick={handleMessageClick}>
								<i className="icons-letter" />
								Mesaj
							</button>
							<If condition={!!(enterTestResultConfig?.isActive && enterTestResultConfig?.formLink)}>
								<div className="btn-tooltip-wrapper">
									<button
										className="btn btn-secondary btn-icon"
										onClick={handleOpenExamForm}>
										<i className="icons-clipboard-check" />
									</button>
									<span className="btn-tooltip">Deneme Sınavı Gir</span>
								</div>
							</If>
						</div>
					</div>

					<div className="student-dashboard__profile-bottom-package-action">
						<button
							className="btn-open-package-info"
							onClick={() => setIsOpenPackageInfoModal(true)}>
							<i className="icons-cap-full" />
							Paket Bilgilerini Gör
						</button>
					</div>
				</div>
			</div>

			{/* Tabs */}
			<div className="student-dashboard__tabs">
				{tabs.map(tab => (
					<div
						key={tab.id}
						className={clsx('student-dashboard__tabs-item', { 'student-dashboard__tabs-item--active': activeTab === tab.id }, { disabled: !tab?.enabled || false })}
						onClick={() => (tab?.enabled ? handleTabClick(tab.id) : {})}>
						{tab.name}
					</div>
				))}
			</div>
			<Choose>
				<When condition={activeTab === 'meetings'}>
					<StudentMeetings studentId={student?.id?.toString() || ''} />
				</When>
				<When condition={activeTab === 'missions'}>
					<StudentMissions
						studentId={Number(student?.id)}
						gradeId={Number(student?.gradeId)}
					/>
				</When>
                <When condition={activeTab === 'support'}>
                    <StudentSupport studentId={Number(student?.id)} studentAvatar={student?.avatar} />
                </When>
                <When condition={activeTab === 'exams'}>
                    <StudentExams studentId={Number(student?.id)} gradeId={Number(student?.gradeId)} />
                </When>
				<Otherwise>
					<StudentPerformance
						studentId={student?.id?.toString() || ''}
						gradeId={Number(student?.gradeId)}
					/>
				</Otherwise>
			</Choose>
			<StudentPackageInfoModal
				isOpen={isOpenPackageInfoModal}
				onClose={() => setIsOpenPackageInfoModal(false)}
				studentId={student?.id || ''}
			/>
		</div>
	);
};

export default StudentDashboardScreen;
